import { CustomListKey, formatDateNoTime, fullSingleLineAddress } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { HoveredMapEntity } from "components/community-search/community-map/types";
import { AddressDisplay } from "components/contact-display/address-display";
import { EmailDisplay } from "components/contact-display/email-display";
import { PhoneDisplay } from "components/contact-display/phone-display";
import { defaultLinkStyle } from "components/ui/link";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import { useCustomList } from "store/selectors/bridge";
import { useUsers } from "store/selectors/hooks";
import { pluralize } from "util/pluralize";
import { NO_NAME } from "util/text";
import { ClientMapStatusPill } from "./client-map-status-pill";
import { preferredEmail, preferredPhone } from "./contact-channel";

export type HomeCareLeadRecord = inferProcedureOutput<
    AppRouter["homeCareLead"]["list"]
>["leads"][number];

interface ClientMapCardListProps {
    leads: HomeCareLeadRecord[];
    total: number;
    setHoveredClient: (hoveredClient: HoveredMapEntity | null) => void;
}

export const ClientMapCardList: React.FC<ClientMapCardListProps> = props => {
    const { leads, total, setHoveredClient } = props;

    const handleEndHover = useCallback(() => {
        setHoveredClient(null);
    }, [setHoveredClient]);

    return (
        <>
            {total > 0 ? (
                <div className="flex items-center justify-between pb-2 lg:pb-4 pt-4 lg:pt-1">
                    <div className="text-sm font-bold">
                        {total} {pluralize(total, "client", "clients")} found
                    </div>
                </div>
            ) : (
                <div className="pb-2 lg:pb-4 pt-4 lg:pt-1 text-sm text-gray-600">
                    No clients found
                </div>
            )}
            <ul className="space-y-4">
                {leads.map(lead => (
                    <li key={lead.id}>
                        <ClientMapCard
                            lead={lead}
                            onHover={setHoveredClient}
                            onEndHover={handleEndHover}
                        />
                    </li>
                ))}
            </ul>
        </>
    );
};

interface ClientMapCardProps {
    lead: HomeCareLeadRecord;
    onHover: (hoveredClient: HoveredMapEntity | null) => void;
    onEndHover: () => void;
}

const ClientMapCard: React.FC<ClientMapCardProps> = props => {
    const { lead, onHover, onEndHover } = props;
    const { getListItemName } = useCustomList(CustomListKey.HomeCareLeadStatus);
    const users = useUsers().users;

    const assignedUser = lead.assignedUserId ? users[lead.assignedUserId] : null;
    const address = fullSingleLineAddress(lead.selfContactEntity);
    const phone = preferredPhone(lead.bestContactEntity, lead.selfContactEntity);
    const email = preferredEmail(lead.bestContactEntity, lead.selfContactEntity);
    const bestContactName = lead.bestContactEntity.name;

    const handleMouseEnter = () => {
        onHover({ entityId: lead.id });
    };

    const handleMouseLeave = () => {
        onEndHover();
    };

    return (
        <div
            className="col-span-1 divide-y divide-gray-200 rounded md:rounded-lg bg-white shadow p-6"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            <div className="flex-1 overflow-auto">
                <div className="flex space-x-2">
                    <div className="flex-1">
                        <h3 className="twoverride text-gray-900 text-lg">
                            <Link
                                to={`/home-care-clients/show/${lead.id}`}
                                className={defaultLinkStyle}>
                                <div>{lead.name || NO_NAME}</div>
                            </Link>
                        </h3>
                        <div className="text-sm text-gray-500">
                            Start of care: {formatDateNoTime(lead.actualStartDate)}
                        </div>
                    </div>
                    <div className="flex-1 text-right">
                        <ClientMapStatusPill
                            statusName={getListItemName(lead.statusListItemId)}
                        />
                    </div>
                </div>
                {bestContactName || phone || email || address ? (
                    <div className="mt-2 text-sm text-gray-500">
                        {bestContactName ? (
                            <div className="text-gray-700">
                                <span className="text-gray-500">Best contact:</span>{" "}
                                <span className="font-medium">{bestContactName}</span>
                            </div>
                        ) : null}
                        {phone ? (
                            <PhoneDisplay
                                value={phone}
                                label={null}
                                entityId={lead.id}
                                entityType={AdvancedSearchEntityType.HomeCareLead}
                            />
                        ) : null}
                        {email ? (
                            <EmailDisplay
                                value={email.address}
                                label={null}
                                optOut={email.optOut}
                                entityType={AdvancedSearchEntityType.HomeCareLead}
                                entityId={lead.id}
                            />
                        ) : null}
                        <AddressDisplay value={address} />
                    </div>
                ) : null}
                <div className="mt-4 border-t border-gray-200 pt-2">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                                Date added
                            </dt>
                            <dd className="mt-1 flex text-sm text-gray-900">
                                {formatDateNoTime(lead.dateAdded)}
                            </dd>
                        </div>
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                                Assigned to
                            </dt>
                            <dd className="mt-1 flex text-sm text-gray-900">
                                {assignedUser ? assignedUser.name : "Unassigned"}
                            </dd>
                        </div>
                        {lead.nextTask?.text ? (
                            <div className="sm:col-span-2">
                                <dt className="text-sm font-medium text-gray-500">
                                    Next task
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                    {lead.nextTask.text}
                                    {lead.nextTask.dueDate ||
                                    lead.nextTask.dueDateTime ? (
                                        <span className="text-gray-500">
                                            {" "}
                                            (
                                            {formatDateNoTime(
                                                lead.nextTask.dueDate ||
                                                    lead.nextTask.dueDateTime,
                                            )}
                                            )
                                        </span>
                                    ) : null}
                                </dd>
                            </div>
                        ) : null}
                    </dl>
                </div>
            </div>
        </div>
    );
};
